import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { SEOHead } from './SEOHead';
import { WebAppSchema, FAQSchema, BreadcrumbSchema } from './StructuredData';
import { seoData, getRelatedToolsData } from './seoData';

/**
 * Complete SEO wrapper for tool pages.
 * Renders SEOHead, structured data, breadcrumbs, FAQ section, and related tools.
 */
export const ToolPageSEO = ({ toolId, children }) => {
  const data = seoData[toolId];
  if (!data) return children || null;

  const relatedTools = getRelatedToolsData(toolId);
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: data.title.split(' - ')[0].replace('Free ', ''), url: data.path },
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title={data.title}
        description={data.description}
        keywords={data.keywords}
        path={data.path}
      />

      {/* Structured Data */}
      <WebAppSchema
        name={data.title.split(' - ')[0].replace('Free ', '')}
        description={data.description}
        url={data.path}
        category="UtilityApplication"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      {data.faqs && <FAQSchema faqs={data.faqs} />}

      {/* Breadcrumb Navigation (visible) */}
      <nav aria-label="Breadcrumb" style={{ marginBottom: '20px' }}>
        <ol
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.85rem',
          }}
        >
          <li>
            <Link
              to="/"
              style={{
                color: 'var(--text-muted)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Home
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
          </li>
          <li>
            <span
              style={{
                color: 'var(--text-primary)',
                fontWeight: 600,
              }}
              aria-current="page"
            >
              {data.title.split(' - ')[0].replace('Free ', '')}
            </span>
          </li>
        </ol>
      </nav>

      {/* Main Tool Content */}
      {children}

      {/* FAQ Section */}
      {data.faqs && data.faqs.length > 0 && (
        <section
          aria-labelledby="faq-heading"
          style={{
            marginTop: '60px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2
            id="faq-heading"
            style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            Frequently Asked Questions
          </h2>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {data.faqs.map((faq, index) => (
              <details
                key={index}
                style={{
                  background: '#ffffff',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  cursor: 'pointer',
                }}
              >
                <summary
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {faq.question}
                  <ChevronRight
                    size={16}
                    style={{
                      color: 'var(--text-muted)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                      marginLeft: '12px',
                    }}
                    className="faq-chevron"
                  />
                </summary>
                <p
                  style={{
                    fontSize: '0.92rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.7,
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-color)',
                  }}
                >
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related Tools Section (Internal Linking) */}
      {relatedTools.length > 0 && (
        <section
          aria-labelledby="related-tools-heading"
          style={{
            marginTop: '48px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2
            id="related-tools-heading"
            style={{
              fontSize: '1.3rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            Related Tools You Might Like
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {relatedTools.map((tool) => (
              <Link
                key={tool.id}
                to={tool.path}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '20px',
                  background: '#ffffff',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                className="related-tool-card"
              >
                <h3
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                  }}
                >
                  {tool.title}
                </h3>
                <p
                  style={{
                    fontSize: '0.82rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {tool.description}
                </p>
                <span
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: 'auto',
                  }}
                >
                  Try Now <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ Chevron rotation styles */}
      <style>{`
        details[open] .faq-chevron {
          transform: rotate(90deg);
        }
        details summary::-webkit-details-marker {
          display: none;
        }
        .related-tool-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -8px rgba(5, 117, 251, 0.15);
          border-color: var(--primary) !important;
        }
      `}</style>
    </>
  );
};

export default ToolPageSEO;
